// ═══════════════════════════════════════════════════════════════════════════
// FILE: src/app/types/ceeq-message.ts
// DATE: 2026-08-08
// PURPOSE: Der Vertrag der Nachrichtenschicht (2do-Queue & Coordination Bus).
//
// EINE FORM FÜR BEIDE WEGE — das ist der Kern dieses Typs:
//   Was über den WebSocket einfliegt und was `GET /api/messages` liefert, ist
//   dieselbe Struktur. Deshalb muss die Page nicht zwei Fälle rendern, je
//   nachdem ob eine Nachricht live ankam oder nachgeladen wurde.
//
// BACKEND-QUELLE (nicht raten, dort nachsehen):
//   nodenanomvp/src/utils/messages/messageQueue.js  [Q-02] buildQueueEntry()
//   nodenanomvp/migrations/2026-08-06__messages-cflx.sql
//
// ⚠️ `id` IST DER VERTRAGSNAME. Es hiess kurzzeitig an einer Stelle
//    `message_id` — vereinheitlicht am 2026-08-07. `id === null` bedeutet:
//    zu diesem Umschlag gibt es KEINE Zeile in `messages` (Direktzustellung
//    aus dem Testpfad). Darauf darf kein seen/ack abgefeuert werden.
//
// MOD 2026-09-26 (PIA, _49 Item #172 Task #660): `cflx` optional/null gemacht —
//    der Typ behauptete „immer da“, TD-04 zeigt alte Redis-Umschläge ohne cflx.
//    Beseitigt 4× NG8107 in messages.component.html, ohne den Schutz ?. zu entfernen.
// ═══════════════════════════════════════════════════════════════════════════

/** Wie verbindlich ist das für mich? Reihenfolge = Dringlichkeit. */
export type Obligation = 'fyi_logged' | 'should_review' | 'must_act';

/** Gewicht als Konfektionsgröße. */
export type Tshirt = 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL';

/**
 * Die vier CFLX-Aussagen. Mindestens eine ist gesetzt — welche, hängt vom
 * Anlass ab (erzwungen in createMessage.js [M-00]).
 *
 *   status_stmnt  WAS ist der Fall?        (sachlich, empfängerneutral)
 *   imessage      WIE bewerte ich das?     (Haltung des Absenders)
 *   appeal        WAS sollst du tun?       (Handlungsaufforderung)
 *   qrepeat       „ich frage erneut"       (Watchdog-Erinnerung)
 */
export interface Cflx {
  status_stmnt: string | null;
  imessage: string | null;
  appeal: string | null;
  qrepeat: string | null;
}

/** Woran hängt die Nachricht? */
export interface MessageAnchor {
  type: string | null;   // item | task | sprint | discussion_post | …
  id: number | null;
}

/** Eine Nachricht, so wie sie über beide Wege ankommt. */
export interface CeeqMessage {
  id: number | null;
  pslot: string | null;
  uuid: string | null;             // an wen — immer meine eigene in der Inbox
  utc: string;                     // ISO-8601
  from: string | null;             // Anzeigename, nur zur Darstellung
  /**
   * Kann fehlen: Queue-Einträge in Redis behalten die Form ihres Schreibzeitpunkts
   * (TD-04). Umschläge von vor der CFLX-Migration (2026-08-06) tragen kein `cflx`.
   * Deshalb immer mit `?.` lesen. (MOD 2026-09-26, _49 Item #172 Task #660)
   */
  cflx?: Cflx | null;
  event_type: string;
  anchor: MessageAnchor;
  obligation: Obligation | null;
  interest_tshirt: Tshirt | null;

  /**
   * WARUM erreicht mich das? Pflichtfeld im Backend (NOT NULL).
   * 🔴 Diese Zeile ist der Grund, warum die Seite benutzt statt weggeklickt
   *    wird — wer nicht nachlesen kann, warum ihn etwas erreicht, traut dem
   *    Posteingang nicht. Niemals ausblenden.
   */
  reason: string | null;

  actor_uuid: string | null;
  context: Record<string, unknown> | null;

  // ── Nur in der Antwort von GET /api/messages (aus message_recipients) ──
  delivered_ws_at?: string | null;
  seen_at?: string | null;
  acked_at?: string | null;
}

/** Antwort von GET /api/messages */
export interface InboxResponse {
  status: string;
  count: number;
  unseen: number;
  unacked: number;
  total: number;
  ws_connected: boolean;
  messages: CeeqMessage[];
}

/** Antwort von GET /api/messages/count */
export interface InboxCounts {
  status: string;
  total: number;
  unseen: number;
  unacked: number;
  must_act_open: number;
  ws_connected: boolean;
}

/** Was der Server über den Socket schickt. */
export type SocketFrame =
  | ({ type: 'message.new' } & CeeqMessage)
  | { type: 'ws.hello'; uuid: string; email: string; server_time: string }
  | { type: 'pong'; server_time: string };

/** Sortierrang: was handeln verlangt, steht oben. */
export const OBLIGATION_RANK: Record<Obligation, number> = {
  must_act: 0,
  should_review: 1,
  fyi_logged: 2,
};

/** Beschriftung in der Oberfläche. Kurz — es steht in einer Zeile mit Text. */
export const OBLIGATION_LABEL: Record<Obligation, string> = {
  must_act: 'MUSS',
  should_review: 'PRÜFEN',
  fyi_logged: 'INFO',
};
